import { useState, useEffect } from 'react';
import MetricCard from '../components/cards/MetricCard';
import api from '../services/api';

// Which lessons students open, whether they reach the end, and whether they
// keep narration on — the ranking for what to narrate / extend next.
// Internal accounts are excluded server-side.

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function pct(v) {
  return v === null || v === undefined ? '—' : `${v}%`;
}

// Drop-off strip: one bar per screen reached (intro … memory card).
function Reached({ counts }) {
  const max = Math.max(1, ...counts);
  const last = counts.length - 1;
  return (
    <div className="flex items-end gap-px h-6" title={counts.map((c, i) => `${i === 0 ? 'intro only' : i === last ? 'finished' : `screen ${i}`}: ${c}`).join('\n')}>
      {counts.map((c, i) => (
        <div
          key={i}
          className={`w-1.5 rounded-sm ${i === last ? 'bg-emerald-500' : 'bg-indigo-300'}`}
          style={{ height: `${Math.max(2, Math.round((c / max) * 24))}px`, opacity: c === 0 ? 0.25 : 1 }}
        />
      ))}
    </div>
  );
}

export default function FocusLearningPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState('all');
  const [showUnopened, setShowUnopened] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const result = await api.getFocusLearningMetrics();
        setData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading Focus Learning data: {error}
        </div>
      </div>
    );
  }

  const s = data?.summary;
  const n = s?.narration;
  const lessons = (data?.lessons || [])
    .filter((l) => subject === 'all' || l.subject === subject)
    .filter((l) => showUnopened || l.opened > 0);
  const unopened = (data?.lessons || []).filter((l) => l.opened === 0).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-teacher-text-primary">Focus Learning</h1>
        <p className="text-teacher-text-secondary">
          Who opens lessons, who reaches the memory card, and whether narration stays on. Internal accounts excluded.
          {data?.computed_at && <span className="text-teacher-text-tertiary"> · computed {new Date(data.computed_at).toLocaleString('en-IN')}</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <MetricCard title="Students who opened a lesson" value={s?.students ?? 0} loading={loading} />
        <MetricCard title="Lesson opens" value={s ? `${s.opens} · ${s.opens_7d} this week` : 0} loading={loading} />
        <MetricCard title="Reached the end" value={s ? `${pct(s.completion_pct)} (${s.completions})` : '—'} loading={loading} />
        <MetricCard
          title="Narration muted"
          value={n ? `${pct(n.muted_pct)} (${n.muted} of ${n.on + n.muted})` : '—'}
          loading={loading}
        />
        <MetricCard title="Lessons touched" value={s ? `${s.lessons_opened} of ${s.lessons_published}` : '—'} loading={loading} />
      </div>

      {n && (
        <p className="text-sm text-teacher-text-secondary mb-4">
          Narration is read from the {s.lessons_with_audio} narrated lessons only: {n.on} opens kept it on, {n.muted} muted it at some point,
          {' '}{n.played} actually heard a clip. Opens from builds before 172 carry no mute flag and are not counted either way.
        </p>
      )}

      <div className="flex items-center gap-3 mb-3">
        {['all', 'physics', 'chemistry', 'mathematics'].map((sub) => (
          <button
            key={sub}
            onClick={() => setSubject(sub)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${subject === sub ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-teacher-text-secondary border-teacher-border'}`}
          >
            {sub === 'all' ? 'All subjects' : SUBJECT_LABEL[sub]}
          </button>
        ))}
        <label className="ml-auto text-sm text-teacher-text-secondary flex items-center gap-2">
          <input type="checkbox" checked={showUnopened} onChange={(e) => setShowUnopened(e.target.checked)} />
          Show lessons nobody opened ({unopened})
        </label>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-teacher-border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-teacher-text-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Lesson</th>
              <th className="text-right px-3 py-3 font-medium">Opened</th>
              <th className="text-right px-3 py-3 font-medium">This week</th>
              <th className="text-right px-3 py-3 font-medium">Finished</th>
              <th className="text-right px-3 py-3 font-medium">Avg progress</th>
              <th className="text-right px-3 py-3 font-medium">Past halfway</th>
              <th className="text-left px-3 py-3 font-medium">How far they got</th>
              <th className="text-right px-3 py-3 font-medium">Narration muted</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-teacher-text-tertiary">Loading…</td></tr>
            )}
            {!loading && lessons.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-teacher-text-tertiary">No lesson opens yet.</td></tr>
            )}
            {lessons.map((l) => (
              <tr key={l.id} className={`border-t border-teacher-border ${l.opened === 0 ? 'text-teacher-text-tertiary' : ''}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-teacher-text-primary">{l.title}</div>
                  <div className="text-xs text-teacher-text-tertiary">
                    {SUBJECT_LABEL[l.subject] || l.subject} · {l.chapter_name} · {l.screen_count} screens{l.has_audio ? ' · narrated' : ''}
                  </div>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{l.opened}</td>
                <td className="px-3 py-3 text-right tabular-nums">{l.opened_7d}</td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {l.completed} <span className="text-teacher-text-tertiary">({pct(l.completion_pct)})</span>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{pct(l.avg_progress_pct)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{pct(l.reached_half_pct)}</td>
                <td className="px-3 py-3"><Reached counts={l.screens_reached} /></td>
                <td className="px-3 py-3 text-right tabular-nums">
                  {l.has_audio
                    ? `${pct(l.narration_muted_pct)} (${l.narration_muted} of ${l.narration_on + l.narration_muted})`
                    : <span className="text-teacher-text-tertiary">no audio</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
